import { useTranslation } from 'react-i18next';
import { ChevronRight, ListChecks } from 'lucide-react';
import type { DashboardStats } from '@/api/types';
import type { DrillDownKind } from './types';

interface ActionItem {
  key: DrillDownKind;
  dot: string;
  count: number;
  labelKey: string;
}

interface ActionCenterCardProps {
  stats: DashboardStats;
  onSelect: (kind: DrillDownKind) => void;
}

export function ActionCenterCard({ stats, onSelect }: ActionCenterCardProps) {
  const { t } = useTranslation();

  const allItems: ActionItem[] = [
    { key: 'OVERDUE_RENT', dot: 'bg-red-500', count: stats.overdueRent, labelKey: 'dashboard.actionOverdueRent' },
    { key: 'STAY_ENDING_SOON', dot: 'bg-orange-500', count: stats.stayEndingSoon, labelKey: 'dashboard.actionStayEndingSoon' },
    { key: 'MAINTENANCE', dot: 'bg-orange-500', count: stats.blockedBeds, labelKey: 'dashboard.actionMaintenance' },
    { key: 'CHECKIN_TODAY', dot: 'bg-blue-500', count: stats.checkingInToday, labelKey: 'dashboard.actionCheckingInToday' },
    { key: 'CHECKOUT_TOMORROW', dot: 'bg-gray-400', count: stats.checkingOutTomorrow, labelKey: 'dashboard.actionCheckingOutTomorrow' },
  ];
  const items = allItems.filter(item => item.count > 0);

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-2">
        <ListChecks size={14} className="text-amber-500" /> {t('dashboard.actionCenter')}
      </h3>
      <p className="text-xs text-gray-400 mb-3">{t('dashboard.actionCenterSubtitle')}</p>

      {items.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">{t('dashboard.noActionItems')}</p>
      ) : (
        <div className="space-y-1.5">
          {items.map(item => (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelect(item.key)}
              className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-gray-50 transition group"
            >
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${item.dot}`} />
              <span className="text-sm text-gray-700 flex-1">
                {t(item.labelKey, { count: item.count })}
              </span>
              <ChevronRight size={16} className="text-gray-300 group-hover:text-gray-500 transition shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
